import { useCallback, useMemo } from 'react';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { useCpPortalMirror } from '../contexts/CpPortalContext';

export interface CpPortalUserView {
  id?: number | null;
  companyName?: string | null;
  clientCode?: string | null;
  role?: string;
  branding?: {
    displayName?: string | null;
    logoUrl?: string | null;
    primaryColor?: string | null;
    hasLogo?: boolean;
  } | null;
  onboardingPhase?: string;
  revenueShareEnabled?: boolean;
  passthroughEnabled?: boolean;
  email?: string | null;
  name?: string | null;
}

export function useCpPortal() {
  const mirror = useCpPortalMirror();
  const clientAuth = useClientAuth();
  const isMirror = !!mirror;

  const getToken = useCallback(() => {
    if (isMirror) return sessionStorage.getItem('portal_token');
    return clientAuth.getToken();
  }, [isMirror, clientAuth]);

  const apiBase = isMirror
    ? `/api/v1/disbursement-clients/${mirror!.clientId}/cp-mirror`
    : '/api/v1/client-portal';

  const authHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, [getToken]);

  const cpPath = useCallback(
    (suffix: string) => {
      const normalized = suffix.startsWith('/') ? suffix : `/${suffix}`;
      if (isMirror) return `/admin/channel-partners/${mirror!.clientId}/portal${normalized}`;
      return `/client${normalized}`;
    },
    [isMirror, mirror],
  );

  const user: CpPortalUserView | null = useMemo(() => {
    if (isMirror) {
      return {
        companyName: mirror!.companyName,
        clientCode: mirror!.clientCode,
        role: mirror!.role,
        branding: mirror!.branding,
        onboardingPhase: mirror!.onboardingPhase,
        revenueShareEnabled: !!mirror!.revenueShareEnabled,
        passthroughEnabled: !!mirror!.passthroughEnabled,
        name: 'MM Admin',
        email: null,
      };
    }
    return clientAuth.user;
  }, [isMirror, mirror, clientAuth.user]);

  const exitPath = isMirror ? '/admin/channel-partners' : '/client/login';

  return {
    isMirror,
    apiBase,
    authHeaders,
    cpPath,
    getToken,
    user,
    mirror,
    exitPath,
    logout: isMirror ? undefined : clientAuth.logout,
  };
}
